import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import DramaCard from '@/components/feature/DramaCard';
import { dramas } from '@/mocks/dramas';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

type SortKey = 'relevance' | 'latest';

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q')?.trim() ?? '';
  const [keyword, setKeyword] = useState(query);
  const [sort, setSort] = useState<SortKey>('relevance');
  const [submitted, setSubmitted] = useState(query);

  useDocumentMeta({
    title: query
      ? `“${query}”搜索结果 - 香草在线观看免费播放电视剧`
      : '搜索电视剧 - 香草在线观看免费播放电视剧',
    description: query
      ? `在香草视频搜索“${query}”相关的电视剧资源，快速定位你想看的高清剧集。`
      : '输入关键词快速搜索你想看的电视剧，支持按剧名、演员、类型检索。',
    keywords: `搜索电视剧,${query || '电视剧搜索'},香草在线观看,在线观看`,
  });

  const results = useMemo(() => {
    const key = submitted.trim().toLowerCase();
    if (!key) return [];
    const matched = dramas.filter((drama) => {
      const haystack = [
        drama.title,
        ...drama.actors,
        drama.director,
        ...drama.types,
        drama.region,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(key);
    });

    if (sort === 'latest') {
      return matched.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }

    return matched.sort((a, b) => {
      const score = (title: string) => {
        const t = title.toLowerCase();
        if (t === key) return 3;
        if (t.includes(key)) return 2;
        return 1;
      };
      const diff = score(b.title) - score(a.title);
      return diff !== 0 ? diff : b.hot - a.hot;
    });
  }, [submitted, sort]);

  const hotRecommend = useMemo(() => [...dramas].sort((a, b) => b.hot - a.hot).slice(0, 4), []);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    setSubmitted(value);
    if (value) {
      setParams({ q: value });
    } else {
      setParams({});
    }
  };

  const hasSearched = submitted.trim().length > 0;

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />
      <main className="flex-1 pt-16 md:pt-20">
        <section className="w-full bg-primary-50 border-b border-primary-100">
          <div className="w-full px-4 md:px-8 py-10 md:py-14">
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">搜索电视剧</h1>
            <p className="mt-2 text-sm text-foreground-600">输入剧名、演员或类型，快速找到你想看的剧集</p>

            <form onSubmit={onSubmit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="例如：都市、悬疑、林知遥…"
                  className="w-full h-12 pl-11 pr-4 rounded-full bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                />
              </div>
              <button
                type="submit"
                className="h-12 px-8 rounded-full bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>

            <div className="mt-4 flex items-center gap-2 flex-wrap text-xs text-foreground-500">
              <span>热门搜索：</span>
              {['都市', '悬疑', '爱情', '古装', '科幻'].map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => {
                    setKeyword(tag);
                    setSubmitted(tag);
                    setParams({ q: tag });
                  }}
                  className="px-2.5 py-1 rounded-full bg-background-50 border border-background-200 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          {hasSearched ? (
            <>
              <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
                <p className="text-sm text-foreground-600">
                  关于 “<strong className="text-foreground-950">{submitted}</strong>” 共找到{' '}
                  <strong className="text-primary-700">{results.length}</strong> 条结果
                </p>
                {results.length > 0 && (
                  <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
                    <button
                      type="button"
                      onClick={() => setSort('relevance')}
                      className={`px-3 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer ${
                        sort === 'relevance' ? 'bg-primary-500 text-background-50' : 'text-foreground-600'
                      }`}
                    >
                      相关度
                    </button>
                    <button
                      type="button"
                      onClick={() => setSort('latest')}
                      className={`px-3 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer ${
                        sort === 'latest' ? 'bg-primary-500 text-background-50' : 'text-foreground-600'
                      }`}
                    >
                      更新时间
                    </button>
                  </div>
                )}
              </div>

              {results.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                  {results.map((drama, index) => (
                    <DramaCard key={drama.id} drama={drama} index={index} />
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-14 flex flex-col items-center text-center px-6">
                  <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                    <i className="ri-search-eye-line text-3xl" />
                  </span>
                  <h3 className="font-heading font-semibold text-base text-foreground-900">未找到相关电视剧</h3>
                  <p className="mt-2 text-sm text-foreground-500 max-w-sm">换个关键词试试，或看看下面的热门推荐内容。</p>
                </div>
              )}

              {results.length === 0 && (
                <section className="mt-10">
                  <h2 className="font-heading font-bold text-lg md:text-2xl text-foreground-950 mb-5">热门推荐</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                    {hotRecommend.map((drama, index) => (
                      <DramaCard key={drama.id} drama={drama} index={index} />
                    ))}
                  </div>
                </section>
              )}
            </>
          ) : (
            <section>
              <h2 className="font-heading font-bold text-lg md:text-2xl text-foreground-950 mb-5">大家都在看</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {hotRecommend.map((drama, index) => (
                  <DramaCard key={drama.id} drama={drama} index={index} />
                ))}
              </div>
              <div className="mt-8 text-center">
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-background-100 border border-background-300 hover:border-primary-300 text-sm font-medium text-foreground-800 transition-colors whitespace-nowrap"
                >
                  <i className="ri-apps-2-line" />
                  浏览全部分类
                </Link>
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}